import axios from 'axios';
import { toast } from 'react-toastify';
import { getBreakUntilMs } from './breakSessionStorage';
import { dispatchGlobalError } from './globalMessageEvents';
import {
  getAuthAccessToken,
  getAuthRefreshToken,
  isDemoPreviewTab,
  setDemoPreviewTabTokens,
  clearDemoPreviewAuthAndBackup,
} from './demoPreviewStorage';
import {
  isMarketingPreviewTab,
  setMarketingPreviewTabTokens,
  clearMarketingPreviewTabSession,
} from './marketingPreviewStorage';

let lastBreakLockToastAt = 0;

function methodIsMutating(method) {
  const m = String(method || 'get').toLowerCase();
  return ['post', 'put', 'patch', 'delete'].includes(m);
}

function urlMatches(configUrl, pattern) {
  if (!configUrl) return false;
  return pattern.test(String(configUrl));
}

/** During an active timed break (local session), block API mutations except punch-in / logout / break completion logs. */
function isMutationAllowedDuringBreak(config) {
  const method = String(config.method || 'get').toLowerCase();
  const url = config.url || '';

  if (urlMatches(url, /\/punchcard\/punch-in(\?|$)/i)) return true;
  if (urlMatches(url, /\/auth\/logout(\?|$)/i)) return true;
  if (urlMatches(url, /\/auth\/refresh-token(\?|$)/i)) return true;

  if (method === 'post' && urlMatches(url, /\/pto\/my-requests(\?|$)/i)) return true;
  if (method === 'post' && urlMatches(url, /\/pto\/approvals\//i)) return true;

  if (method === 'post' && urlMatches(url, /\/activities\/quick(\?|$)/i)) {
    const data = config.data;
    const at = data && typeof data === 'object' ? data.activity_type : null;
    if (at === 'break_overrun') return true;
  }

  return false;
}

/**
 * Ensure REST calls hit the `/api` prefix the backend uses (`app.use('/api/chat', ...)` etc.).
 * If REACT_APP_API_URL is only `http://host:port`, axios would call `/chat/...` on the host root → 404 "Endpoint not found".
 */
function normalizeApiBaseUrl(url) {
  if (url == null || url === '') return url;
  const s = String(url).trim();
  const noTrail = s.replace(/\/+$/, '');
  if (/\/api$/i.test(noTrail)) return noTrail;

  try {
    const hasProtocol = /^[a-z][a-z\d+\-.]*:/i.test(s);
    if (hasProtocol || s.startsWith('//')) {
      const u = new URL(hasProtocol ? s : `http:${s}`);
      const pathname = (u.pathname || '').replace(/\/+$/, '') || '';
      if (pathname === '' || pathname === '/') {
        return `${u.origin}/api`;
      }
      return noTrail;
    }
    if (s.startsWith('/')) {
      const t = noTrail;
      if (t === '/api') return t;
      return `${t}/api`.replace(/\/{2,}/g, '/');
    }
    return normalizeApiBaseUrl(`http://${s}`);
  } catch {
    return noTrail;
  }
}

/**
 * Resolve at request time. Prefer `window.__ERP4C_API_BASE__` from public/index.html (same-origin
 * `/api` behind nginx). That fixes stale bundles and avoids calling a second port. Authenticated
 * routes need the Bearer token forwarded — nginx must pass Authorization (see nginx-full.conf).
 */
function resolveApiBaseUrl() {
  const win = typeof window !== 'undefined' ? window : null;

  if (process.env.NODE_ENV === 'development' && win) {
    const fromEnv = process.env.REACT_APP_API_URL;
    if (fromEnv != null && String(fromEnv).trim() !== '') {
      return normalizeApiBaseUrl(fromEnv);
    }
  }

  if (
    win &&
    typeof win.__ERP4C_API_BASE__ === 'string' &&
    win.__ERP4C_API_BASE__.trim() !== ''
  ) {
    return normalizeApiBaseUrl(win.__ERP4C_API_BASE__.trim());
  }

  if (process.env.NODE_ENV === 'development' && win) {
    return normalizeApiBaseUrl(`${win.location.origin}/api`);
  }

  if (win) {
    const fromEnv = process.env.REACT_APP_API_URL;
    if (fromEnv != null && String(fromEnv).trim() !== '') {
      const e = String(fromEnv).trim();
      if (e.startsWith('/')) {
        return normalizeApiBaseUrl(`${win.location.origin}${e}`);
      }
      return normalizeApiBaseUrl(e);
    }
    // Same-origin /api (nginx in-cluster, or CRA dev proxy). Never use host:10001 here — the browser
    // cannot reach the backend Node port when only kubectl port-forward 9000→frontend is used.
    return normalizeApiBaseUrl(`${win.location.origin}/api`);
  }

  return normalizeApiBaseUrl(
    process.env.REACT_APP_API_URL || 'http://localhost:10001/api'
  );
}

export function getApiBaseUrl() {
  return resolveApiBaseUrl();
}

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Do not hard-redirect to /login — anonymous pages and session bootstrap must survive stale tokens. */
function shouldSkipLoginRedirect(originalRequest) {
  if (originalRequest?.skipAuthRedirect) return true;
  const rel = originalRequest?.url || '';
  const full = `${originalRequest?.baseURL || ''}${rel}`;
  if (/\/auth\/profile(\?|$)/i.test(full) || /auth\/profile/i.test(rel)) return true;
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname || '';
  if (p === '/' || p === '/query' || p.startsWith('/query/') || p === '/public' || p.startsWith('/public/')) return true;
  if (['/login', '/signup', '/verify-otp', '/verify-email'].includes(p)) return true;
  return false;
}

// Request interceptor — base URL every time (fixes stale/wrong API_URL at module load; matches localhost vs 127.0.0.1)
api.interceptors.request.use(
  (config) => {
    config.baseURL = resolveApiBaseUrl();
    const token = getAuthAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // multipart/form-data must set Content-Type with a boundary; axios does that only if we do NOT force application/json
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers;
      if (h && typeof h.delete === 'function') {
        h.delete('Content-Type');
      } else {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }

    if (
      typeof window !== 'undefined' &&
      getBreakUntilMs() != null &&
      methodIsMutating(config.method) &&
      !isMutationAllowedDuringBreak(config) &&
      !config.skipBreakLock
    ) {
      const now = Date.now();
      if (now - lastBreakLockToastAt > 2200) {
        lastBreakLockToastAt = now;
        toast.warning(
          'You are on a break. End the break (punch in or cancel break early) before saving or changing data.'
        );
      }
      const err = new Error('BREAK_SESSION_ACTIVE');
      err.code = 'BREAK_SESSION_ACTIVE';
      err.skipErrorToast = true;
      return Promise.reject(err);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors and token refresh
let lastErrorToastKey = '';
let lastErrorToastAt = 0;
const ERROR_TOAST_DEDUP_MS = 8000;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isLoginRequest = originalRequest?.url?.includes('/auth/login');

    if (isLoginRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = getAuthRefreshToken();
      if (refreshToken) {
        try {
          const base = resolveApiBaseUrl();
          const response = await axios.post(`${base}/auth/refresh-token`, {
            refreshToken,
          });

          if (response.data.success) {
            const { token, refreshToken: newRefreshToken } = response.data.data;
            if (isMarketingPreviewTab()) {
              setMarketingPreviewTabTokens(token, newRefreshToken);
            } else if (isDemoPreviewTab()) {
              setDemoPreviewTabTokens(token, newRefreshToken);
            } else {
              localStorage.setItem('token', token);
              localStorage.setItem('refreshToken', newRefreshToken);
            }

            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          if (isMarketingPreviewTab()) {
            clearMarketingPreviewTabSession();
          } else if (isDemoPreviewTab()) {
            clearDemoPreviewAuthAndBackup();
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
          }
          if (!shouldSkipLoginRedirect(originalRequest)) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      } else {
        if (isMarketingPreviewTab()) {
          clearMarketingPreviewTabSession();
        } else if (isDemoPreviewTab()) {
          clearDemoPreviewAuthAndBackup();
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
        if (!shouldSkipLoginRedirect(originalRequest)) {
          window.location.href = '/login';
        }
      }
    }

    if (
      !axios.isCancel(error) &&
      error.response?.status !== 401 &&
      !originalRequest?.skipErrorToast &&
      error.code !== 'BREAK_SESSION_ACTIVE'
    ) {
      let message = error.response?.data?.message;
      if (!message) {
        const noResponse = !error.response;
        const networkish =
          noResponse &&
          (error.code === 'ERR_NETWORK' ||
            error.code === 'ECONNABORTED' ||
            /network/i.test(String(error.message || '')));
        if (networkish) {
          const base = resolveApiBaseUrl();
          message = `Cannot reach the API (${base}). Run ./01-port-forward-docker-desktop-k8s-local-2-local-laptop.sh and open the UI on the same host/port (only 9000 is forwarded; /api is proxied by nginx to the backend).`;
        } else if (error.response?.status === 502) {
          message =
            'API gateway error (502). The backend may be restarting — wait a moment and refresh.';
        } else {
          message = error.message && String(error.message) !== 'Error' ? error.message : 'An error occurred';
        }
      }
      const toastKey = `${error.response?.status || ''}|${message}`;
      const now = Date.now();
      const shouldToast =
        toastKey !== lastErrorToastKey || now - lastErrorToastAt > ERROR_TOAST_DEDUP_MS;
      if (shouldToast) {
        lastErrorToastKey = toastKey;
        lastErrorToastAt = now;
        toast.error(message);
      }
      if (!originalRequest?.skipGlobalMessage && typeof window !== 'undefined' && shouldToast) {
        const cfg = originalRequest || {};
        const rel = cfg.url || '';
        const base = cfg.baseURL || '';
        const requestUrl = rel && !/^https?:/i.test(rel) ? `${base}${rel}` : rel || base;
        dispatchGlobalError(message, {
          source: 'api',
          httpMethod: String(cfg.method || 'get').toUpperCase(),
          requestUrl,
          httpStatus: error.response?.status,
        });
      }
    }

    return Promise.reject(error);
  }
);

export const apiGet = async (url, params = {}) => {
  const response = await api.get(url, { params });
  return response.data;
};

export const apiPost = async (url, data = {}) => {
  const response = await api.post(url, data);
  return response.data;
};

export const apiPut = async (url, data = {}) => {
  const response = await api.put(url, data);
  return response.data;
};

export const apiDelete = async (url) => {
  const response = await api.delete(url);
  return response.data;
};

export const apiUpload = async (url, file, additionalData = {}) => {
  const formData = new FormData();
  formData.append('file', file);

  Object.keys(additionalData).forEach((key) => {
    formData.append(key, additionalData[key]);
  });

  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export default api;
